import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Attribution } from '../../components/Attribution.js'
import { Feedback } from '../../components/Feedback.js'
import { DefaultSkinProvider } from '../../skin/SkinContext.js'

export interface ArticleSection {
  id: string
  title: string
}

/**
 * The shell every page of prose on this site wears.
 *
 * Factored out of the rules page, which had the layout first and is now the
 * largest thing wearing it: back nav, title, standfirst, a jump list that
 * sticks beside the sections on a wide screen, and the licence footer. A second
 * page copying that by hand would have drifted by the third one.
 *
 * `<Attribution>` is not optional and not a prop. It is how this adaptation
 * satisfies the CC BY-NC-SA terms it ships under, and a page that could be
 * built without it is a page somebody will eventually build without it.
 */
export function Article({
  title,
  standfirst,
  sections,
  children,
}: {
  title: string
  standfirst: ReactNode
  /** Omit on a short page: a jump list over three headings is furniture. */
  sections?: readonly ArticleSection[]
  children: ReactNode
}) {
  const navigate = useNavigate()
  // React Router stamps an index on every entry it pushed. Zero means this tab
  // opened straight onto this page - a shared link, a search result, a bookmark
  // - and there is nothing of ours behind it to go back to. Zero is also the
  // right answer while prerendering, where there is no history at all.
  const idx =
    typeof window === 'undefined'
      ? 0
      : ((window.history.state as { idx?: number } | null)?.idx ?? 0)

  return (
    <DefaultSkinProvider>
      <main className="shell rules">
        <nav className="rules__nav">
          <button
            type="button"
            className="btn btn--ghost btn--small"
            onClick={() => (idx > 0 ? navigate(-1) : navigate('/'))}
            data-testid="article-back"
          >
            ← Back
          </button>
        </nav>

        <header className="rules__head">
          <h1 className="rules__title">{title}</h1>
          <p className="rules__sub">{standfirst}</p>
        </header>

        {sections && sections.length > 0 && (
          <nav className="rules__toc" aria-label="Sections">
            {sections.map((s) => (
              <a key={s.id} className="rules__toc-link" href={`#${s.id}`}>
                {s.title}
              </a>
            ))}
          </nav>
        )}

        {/*
          One box around every section, so that on a desktop the jump list has a
          single tall neighbour to stay stuck against while it scrolls past.
        */}
        <div className="rules__sections">{children}</div>

        {/*
          Two things at the foot of a page of prose: the way back, and the way to
          say something about it. Here rather than in `<Attribution>` - which is
          the obvious shared home and the wrong one, because it also renders at
          the foot of every in-game screen and on the table board, and feedback
          is deliberately not offered in either place.
        */}
        <p className="rules__foot">
          <Link to="/">Back to the start</Link> · <Feedback />
        </p>

        <Attribution />
      </main>
    </DefaultSkinProvider>
  )
}
